import {type FlagProps} from 'platform/components';
import {Box, Grid, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNotNil, partition, without} from 'ramda';

import type {
  ConfiguratorBundleResponseBody,
  ConfiguratorFeatureResponseBody,
  ConfiguratorPackageResponseBody,
  EvaluateConfigurationResponseBody,
  EvaluationItemResponseBody,
} from '@dms/api/new-vehicle';
import {FEATURE_CATEGORIES, getNewVehicleEnumLabel, OptionCard} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';

import type {ConfiguratorFormValues} from '../../NewVehicleConfigurator';
import {getExclusionCheck} from '../../utils/getExclusionCheck';
import {getFeatureOffer} from '../../utils/getFeatureOffer';
import {getVariantLabel} from '../../utils/getVariantLabel';
import {getWheelSet, type WheelSet} from '../../utils/getWheelSet';
import {isStandardFeature} from '../../utils/isStandardFeature';
import {SectionLabel, StepIntro} from '../StepIntro';
import {WheelSetGroup} from '../WheelSetGroup';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const tValues = (key: string, options: {defaultValue: string; [value: string]: string}) =>
  i18n.t(key, options);
const notAvailableFor = (trim: string) =>
  tValues('entity.newVehicle.labels.notAvailableForTrim', {
    defaultValue: 'Not available for {trim}',
    trim,
  });

type Props = {
  bundle?: ConfiguratorBundleResponseBody;
  evaluation?: EvaluateConfigurationResponseBody;
  /** `evaluation` belongs to an earlier selection and a fresh result is on its way. */
  isEvaluationStale?: boolean;
  form: UseFormReturn<ConfiguratorFormValues>;
  canSelect: boolean;
  factoryFreezeReason?: string;
};

const toggleId = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];

const reason = (item: EvaluationItemResponseBody) =>
  item.reason ?? getNewVehicleEnumLabel('reasonCode', item.reasonCode ?? '', t);

const getPricePair = (
  catalogue: Pick<ConfiguratorFeatureResponseBody, 'priceNet' | 'priceGross'>,
  evaluated?: EvaluationItemResponseBody
) =>
  isNotNil(evaluated?.priceNet) && isNotNil(evaluated?.priceGross)
    ? {net: evaluated.priceNet, gross: evaluated.priceGross}
    : {net: catalogue.priceNet, gross: catalogue.priceGross};

const dealerFitFlags = (feature?: ConfiguratorFeatureResponseBody): FlagProps[] | undefined =>
  feature?.fitStage === 'dealer'
    ? [
        {
          label: getNewVehicleEnumLabel('fitStage', feature.fitStage, t),
          colorScheme: 'neutral',
          isSubtle: true,
        },
      ]
    : undefined;

function Section(props: {title: string; count: number; intro?: string; children: React.ReactNode}) {
  return (
    <section aria-label={props.title}>
      <VStack spacing={3}>
        <VStack spacing={1}>
          <SectionLabel label={props.title} count={props.count} />
          <Show when={props.intro}>
            <Text size="xSmall" color="secondary">
              {props.intro}
            </Text>
          </Show>
        </VStack>
        {props.children}
      </VStack>
    </section>
  );
}

function PackageMembers(props: {features: ConfiguratorFeatureResponseBody[]}) {
  return (
    <VStack spacing={1}>
      {props.features.map((feature) => (
        <HStack key={feature.id} spacing={1} align="flex-start">
          <Box paddingTop={1} flexShrink={0}>
            <Icon
              value="navigation/check"
              size={3}
              color={feature.standaloneOrderable ? 'text.secondary' : 'palettes.blue.60.100'}
            />
          </Box>
          <Text size="small" color={feature.standaloneOrderable ? 'secondary' : 'link'}>
            {feature.standaloneOrderable
              ? feature.name
              : `${feature.name} · ${t('entity.newVehicle.labels.exclusive', 'exclusive')}`}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
}

type FeatureGroupSectionProps = {
  category: (typeof FEATURE_CATEGORIES)[number];
  features: ConfiguratorFeatureResponseBody[];
  wheelSet: WheelSet;
  currency: string;
  canSelect: boolean;
  renderFeature: (feature: ConfiguratorFeatureResponseBody) => React.ReactNode;
};

/** One feature category; its wheels form the single-choice wheel set group. */
function FeatureGroupSection(props: FeatureGroupSectionProps) {
  const [groupWheels, otherFeatures] = partition(
    (feature) => props.wheelSet.wheelIds.has(feature.id),
    props.features
  );
  return (
    <Section
      title={getNewVehicleEnumLabel('featureCategory', props.category, t)}
      count={props.features.length}
    >
      <Show when={groupWheels.length > 0}>
        <WheelSetGroup
          wheelSet={props.wheelSet}
          currency={props.currency}
          canSelect={props.canSelect}
        >
          {groupWheels.map(props.renderFeature)}
        </WheelSetGroup>
      </Show>
      <Show when={otherFeatures.length > 0}>
        <Grid columns={[1, 1, 2, 2]} spacing={3}>
          {otherFeatures.map(props.renderFeature)}
        </Grid>
      </Show>
    </Section>
  );
}

type UnavailableSectionProps = {
  bundle: ConfiguratorBundleResponseBody;
  items: EvaluationItemResponseBody[];
  canSelect: boolean;
  factoryFreezeReason?: string;
  selectedFeatureIds: string[];
  selectedPackageIds: string[];
  /** The package cannot be added for a reason other than the spec freeze. */
  isPackageBlocked: (item: ConfiguratorPackageResponseBody) => boolean;
  toggle: (name: 'featureIds' | 'packageIds', id: string, selected: string[]) => void;
};

function UnavailableSection(props: UnavailableSectionProps) {
  return (
    <Show when={props.items.length > 0}>
      <Section
        title={t('entity.newVehicle.labels.notAvailable', 'Not available for this configuration')}
        count={props.items.length}
        intro={t(
          'entity.newVehicle.labels.notAvailableIntro',
          'Change the selection or use the suggested resolution'
        )}
      >
        <Grid columns={[1, 1, 2, 2]} spacing={3}>
          {props.items.map((item) => {
            const feature = props.bundle.features.find(({id}) => id === item.id);
            const packageItem = props.bundle.packages.find(({id}) => id === item.id);
            const packageForFeature = props.bundle.packages.find(({memberFeatureIds}) =>
              memberFeatureIds.includes(item.id)
            );
            // Reason codes are the only machine interface; `reason` is display text (D47).
            const resolutionBlockedByFreeze = Boolean(
              props.factoryFreezeReason && item.reasonCode === 'package_only' && packageForFeature
            );
            const actions =
              item.reasonCode === 'package_only' && packageForFeature
                ? [
                    {
                      title: `${t('general.actions.open', 'Open')} ${packageForFeature.name}`,
                      onClick: () =>
                        props.toggle('packageIds', packageForFeature.id, props.selectedPackageIds),
                      isDisabled:
                        !props.canSelect ||
                        resolutionBlockedByFreeze ||
                        props.isPackageBlocked(packageForFeature),
                    },
                  ]
                : undefined;
            const isSelected =
              props.selectedFeatureIds.includes(item.id) ||
              props.selectedPackageIds.includes(item.id);
            return (
              <OptionCard
                key={item.id}
                title={
                  feature?.name ??
                  packageItem?.name ??
                  t('entity.newVehicle.labels.unavailableSelection', 'Unavailable selection')
                }
                currency={props.bundle.currency}
                flags={dealerFitFlags(feature)}
                isSelected={isSelected}
                isDisabled={!props.canSelect || !isSelected}
                isLocked={false}
                reason={resolutionBlockedByFreeze ? props.factoryFreezeReason : reason(item)}
                actions={actions}
                onToggle={() =>
                  props.toggle(
                    feature ? 'featureIds' : 'packageIds',
                    item.id,
                    feature ? props.selectedFeatureIds : props.selectedPackageIds
                  )
                }
              />
            );
          })}
        </Grid>
      </Section>
    </Show>
  );
}

export function OptionsPackages(props: Props) {
  const selectedFeatureIds = useWatch({control: props.form.control, name: 'featureIds'}) ?? [];
  const selectedPackageIds = useWatch({control: props.form.control, name: 'packageIds'}) ?? [];
  const trimId = useWatch({control: props.form.control, name: 'trimId'});
  const variantId = useWatch({control: props.form.control, name: 'variantId'});
  if (!props.bundle) {
    return <Text>{t('general.labels.loading', 'Loading…')}</Text>;
  }
  const bundle = props.bundle;
  const evaluationById = new Map(props.evaluation?.items.map((item) => [item.id, item]));
  const evaluatedAutoAddedIds = new Set(props.evaluation?.autoAddedSelectionIds ?? []);
  // While the evaluator catches up, package members follow their package at once, so toggling a
  // package does not leave its members showing the previous answer for a round trip.
  // ponytail: a member also auto-added by another rule briefly drops out when its package is
  // cleared; it returns with the fresh result.
  const packageMemberIds = new Set(bundle.packages.flatMap((item) => item.memberFeatureIds));
  const isPackageInTrim = (item: ConfiguratorPackageResponseBody) =>
    item.applicableTrimIds.length === 0 || item.applicableTrimIds.includes(trimId);
  const buildMemberIds = bundle.packages
    .filter((item) => selectedPackageIds.includes(item.id) || evaluatedAutoAddedIds.has(item.id))
    .filter(isPackageInTrim)
    .flatMap((item) => item.memberFeatureIds);
  const autoAddedIds = props.isEvaluationStale
    ? new Set([
        ...[...evaluatedAutoAddedIds].filter((id) => !packageMemberIds.has(id)),
        ...buildMemberIds,
      ])
    : evaluatedAutoAddedIds;
  // The bundle covers the whole model year, but only the evaluator applies trim and variant
  // applicability and it reports availability for *selected* items. Without applying the
  // catalogue's own rules here, this step offers items the evaluator invalidates as `not_in_trim`
  // the moment they are picked, so the selection bounces straight back off. Step 4 disables
  // out-of-trim upholsteries the same way.
  //
  // The evaluator skips assignment applicability for members of an in-trim package in the build
  // (`buildMemberIds`; an out-of-trim package supplies nothing), even once a save echoes them into
  // `featureIds`, so they are never held back for the variant.
  const getOffer = (feature: ConfiguratorFeatureResponseBody) => {
    const offer = getFeatureOffer(feature, trimId, variantId);
    return offer === 'notForVariant' && buildMemberIds.includes(feature.id) ? 'offered' : offer;
  };
  const isOffered = (name: 'featureIds' | 'packageIds', id: string) => {
    if (name === 'featureIds') {
      const feature = bundle.features.find((item) => item.id === id);
      return feature ? getOffer(feature) === 'offered' : true;
    }
    const item = bundle.packages.find((entry) => entry.id === id);
    return item ? isPackageInTrim(item) : true;
  };
  const trimName =
    bundle.trims.find(({id}) => id === trimId)?.name ??
    t('entity.newVehicle.labels.trimLevel', 'Trim level');
  const notInTrimReason = notAvailableFor(trimName);
  // Names the trim and engine, since the variant is what rules the feature out.
  const notForVariantReason = notAvailableFor(getVariantLabel(variantId, bundle) ?? trimName);
  const toggle = (
    name: 'featureIds' | 'packageIds',
    id: string,
    selected: string[],
    next = toggleId(selected, id)
  ) =>
    // Rejected clicks stop here, before the conflict is computed or anything is written.
    match(true)
      .when(
        () => !props.canSelect,
        () => undefined
      )
      .when(
        () =>
          Boolean(props.factoryFreezeReason) &&
          (name === 'packageIds' ||
            bundle.features.find((item) => item.id === id)?.fitStage === 'factory'),
        () => undefined
      )
      // Adding an id the trim or variant does not offer only earns an immediate `not_in_trim`
      // invalidation. Guarding the single toggle chokepoint also covers the resolution buttons
      // below and the wheel picks. Removing stays allowed so a selection that fell out of the
      // offer can still be cleared.
      .when(
        () => !selected.includes(id) && !isOffered(name, id),
        () => undefined
      )
      .otherwise(() => {
        // A package that brings a wheel replaces the picked one at once, not after an evaluator
        // reject.
        const addedPackage =
          name === 'packageIds' && !selected.includes(id)
            ? bundle.packages.find((item) => item.id === id)
            : undefined;
        // Last click wins (D71): what this click adds replaces its exclusion partners in the same
        // update, so the evaluator never sees both ends and drops both.
        const addedFeatureIds =
          next.includes(id) && !selected.includes(id)
            ? (addedPackage?.memberFeatureIds ?? [id])
            : [];
        const conflict = getConflict(addedFeatureIds);
        if (conflict.isBlocked) {
          return;
        }
        const nextFeatureIds =
          name === 'featureIds' ? next : wheelSet.getCandidateFeatureIds(addedFeatureIds);
        const nextPackageIds = name === 'packageIds' ? next : selectedPackageIds;
        props.form.setValue('featureIds', without(conflict.removedFeatureIds, nextFeatureIds), {
          shouldDirty: true,
        });
        props.form.setValue('packageIds', without(conflict.removedPackageIds, nextPackageIds), {
          shouldDirty: true,
        });
      });
  const wheelSet = getWheelSet({
    bundle,
    trimId,
    selectedFeatureIds,
    selectedPackageIds,
    autoAddedIds,
    canSelect: props.canSelect,
    factoryFreezeReason: props.factoryFreezeReason,
    setFeatureIds: (featureIds) =>
      props.form.setValue('featureIds', featureIds, {shouldDirty: true}),
    pickWheel: (wheelId, featureIds) =>
      toggle('featureIds', wheelId, selectedFeatureIds, featureIds),
  });
  const {getWheelProps, isBlockedByWheelLock} = wheelSet;
  const getConflict = getExclusionCheck({
    bundle,
    variantId,
    selectedPackageIds,
    autoAddedIds,
    factoryFreezeReason: props.factoryFreezeReason,
    wheelSet,
  });
  const isUnavailable = (id: string) => evaluationById.get(id)?.availability === 'unavailable';
  const unavailable =
    props.evaluation?.items.filter((item) => item.availability === 'unavailable') ?? [];
  const availableFeatures = bundle.features.filter((feature) => !isUnavailable(feature.id));
  // Closed BE enum in a fixed configurator order; empty groups are hidden.
  const featureGroups = FEATURE_CATEGORIES.map((category) => ({
    category,
    features: availableFeatures.filter((feature) => feature.category === category),
  })).filter((group) => group.features.length > 0);
  // STANDARD features are included by the trim, not options (D72): never selectable or counted.
  const standardFeatureIds = new Set(
    bundle.features.filter((feature) => isStandardFeature(feature, trimId)).map(({id}) => id)
  );
  const selectedCount =
    selectedFeatureIds.filter((id) => !standardFeatureIds.has(id)).length +
    selectedPackageIds.length;

  const renderFeature = (feature: ConfiguratorFeatureResponseBody) => {
    const isStandard = standardFeatureIds.has(feature.id);
    const isFactoryFrozen =
      feature.fitStage === 'factory' && Boolean(props.factoryFreezeReason) && !isStandard;
    const offer = getOffer(feature);
    const wheel = getWheelProps(feature);
    const isSelected = isNotNil(wheel)
      ? wheel.isSelected
      : isStandard || selectedFeatureIds.includes(feature.id) || autoAddedIds.has(feature.id);
    // Clicking replaces a selected partner; the hint says what goes. Standard equipment never
    // conflicts: the trim includes it, so it is never in the selection and its exclusion rules never
    // fire; the standard wheel only clears the picked wheels.
    const conflict = isSelected || isStandard ? undefined : getConflict([feature.id]);
    return (
      <OptionCard
        key={feature.id}
        title={feature.name}
        price={
          isStandard
            ? undefined
            : {...getPricePair(feature, evaluationById.get(feature.id)), basis: bundle.priceBasis}
        }
        currency={bundle.currency}
        priceLabel={isStandard ? t('entity.newVehicle.labels.included', 'Included') : undefined}
        flags={dealerFitFlags(feature)}
        control={wheel?.control}
        name={wheel?.name}
        isSelected={isSelected}
        // The standard wheel stays clickable: picking it clears the upgrade.
        isDisabled={
          !props.canSelect ||
          (isStandard && !wheel) ||
          isFactoryFrozen ||
          (offer !== 'offered' && !isSelected) ||
          isNotNil(wheel?.lockReason) ||
          Boolean(conflict?.isBlocked)
        }
        isLocked={!isStandard && autoAddedIds.has(feature.id)}
        reason={match({wheel, isFactoryFrozen, offer})
          .with({wheel: {lockReason: Pattern.string}}, ({wheel: {lockReason}}) => lockReason)
          .with({isFactoryFrozen: true}, () => props.factoryFreezeReason)
          .with({offer: 'notInTrim'}, () => notInTrimReason)
          .with({offer: 'notForVariant'}, () => notForVariantReason)
          .otherwise(() => conflict?.reason)}
        onToggle={() =>
          isNotNil(wheel)
            ? wheelSet.selectWheel(feature)
            : toggle('featureIds', feature.id, selectedFeatureIds)
        }
      />
    );
  };
  const renderPackageOnly = (feature: ConfiguratorFeatureResponseBody) => {
    const parent = bundle.packages.find(({memberFeatureIds}) =>
      memberFeatureIds.includes(feature.id)
    );
    const isIncluded = selectedFeatureIds.includes(feature.id) || autoAddedIds.has(feature.id);
    const wheel = getWheelProps(feature);
    const conflict =
      isNotNil(parent) && !isIncluded && !selectedPackageIds.includes(parent.id)
        ? getConflict(parent.memberFeatureIds)
        : undefined;
    return (
      <OptionCard
        key={feature.id}
        title={feature.name}
        control={wheel?.control}
        name={wheel?.name}
        description={
          parent
            ? tValues('entity.newVehicle.labels.onlyShipsInside', {
                defaultValue: 'Only ships inside {package}',
                package: parent.name,
              })
            : undefined
        }
        currency={bundle.currency}
        priceLabel={t('entity.newVehicle.labels.packageOnly', 'Package-only')}
        flags={dealerFitFlags(feature)}
        isSelected={isNotNil(wheel) ? wheel.isSelected : isIncluded}
        isDisabled={!isIncluded}
        isLocked={isIncluded}
        reason={
          wheel?.lockReason ??
          props.factoryFreezeReason ??
          (parent && !isPackageInTrim(parent) ? notInTrimReason : undefined) ??
          conflict?.reason
        }
        actions={
          parent && !selectedPackageIds.includes(parent.id)
            ? [
                {
                  title: `${t('general.actions.add', 'Add')} ${parent.name}`,
                  onClick: () => toggle('packageIds', parent.id, selectedPackageIds),
                  isDisabled:
                    !props.canSelect ||
                    Boolean(props.factoryFreezeReason) ||
                    !isPackageInTrim(parent) ||
                    isBlockedByWheelLock(parent) ||
                    Boolean(conflict?.isBlocked),
                },
              ]
            : undefined
        }
        onToggle={() => undefined}
      />
    );
  };
  const renderPackage = (item: ConfiguratorPackageResponseBody) => {
    const isSelected = selectedPackageIds.includes(item.id) || autoAddedIds.has(item.id);
    const isPackageAvailable = isPackageInTrim(item);
    const conflict = isSelected ? undefined : getConflict(item.memberFeatureIds);
    const members = item.memberFeatureIds
      .map((id) => bundle.features.find((feature) => feature.id === id))
      .filter((feature): feature is ConfiguratorFeatureResponseBody => Boolean(feature));
    const exclusiveCount = members.filter((feature) => !feature.standaloneOrderable).length;
    return (
      <OptionCard
        key={item.id}
        title={item.name}
        description={
          <VStack spacing={2}>
            <Show when={item.description}>
              <Text size="small" color="secondary">
                {item.description}
              </Text>
            </Show>
            <PackageMembers features={members} />
            <Text size="xSmall" color="tertiary">
              {`${members.length} ${t('entity.newVehicle.labels.features', 'features')}${exclusiveCount ? ` · ${exclusiveCount} ${t('entity.newVehicle.labels.exclusive', 'exclusive')}` : ''}`}
            </Text>
          </VStack>
        }
        price={{...getPricePair(item, evaluationById.get(item.id)), basis: bundle.priceBasis}}
        currency={bundle.currency}
        flags={[
          {
            label: t('entity.newVehicle.labels.package', 'Package'),
            colorScheme: 'purple',
            isSubtle: true,
          },
        ]}
        isSelected={isSelected}
        isDisabled={
          !props.canSelect ||
          Boolean(props.factoryFreezeReason) ||
          (!isPackageAvailable && !isSelected) ||
          (isUnavailable(item.id) && !isSelected) ||
          isBlockedByWheelLock(item) ||
          Boolean(conflict?.isBlocked)
        }
        isLocked={autoAddedIds.has(item.id)}
        reason={match({
          freezeReason: props.factoryFreezeReason,
          lockReason: isBlockedByWheelLock(item) ? wheelSet.wheelLock?.reason : undefined,
          evaluated: evaluationById.get(item.id),
          isPackageAvailable,
        })
          .with({freezeReason: Pattern.string}, ({freezeReason}) => freezeReason)
          .with({lockReason: Pattern.string}, ({lockReason}) => lockReason)
          .with({evaluated: {availability: 'unavailable'}}, ({evaluated}) => reason(evaluated))
          .with({isPackageAvailable: false}, () => notInTrimReason)
          .otherwise(() => conflict?.reason)}
        onToggle={() => toggle('packageIds', item.id, selectedPackageIds)}
      />
    );
  };

  // STANDARD equipment is included even when it is package-only on other trims.
  const renderGroupedFeature = (feature: ConfiguratorFeatureResponseBody) =>
    feature.standaloneOrderable || standardFeatureIds.has(feature.id)
      ? renderFeature(feature)
      : renderPackageOnly(feature);

  return (
    <VStack spacing={6}>
      <StepIntro
        title={t('entity.newVehicle.steps.optionsPackagesTitle', 'Step 3 — Options & packages')}
        description={t(
          'entity.newVehicle.steps.optionsPackagesIntro',
          'Add extras to the build. Package-only items ship inside their parent package; bundle savings are computed against the à-la-carte value.'
        )}
        aside={
          <Box flexShrink={0}>
            <Text size="small" color="secondary" noWrap>
              {`${selectedCount} ${t('general.labels.selected', 'selected')}`}
            </Text>
          </Box>
        }
      />
      <Show when={bundle.packages.length > 0}>
        <Section
          title={t('entity.newVehicle.catalogue.packages', 'Packages')}
          count={bundle.packages.length}
          intro={t(
            'entity.newVehicle.labels.packagesIntro',
            'Combine popular equipment in one selection'
          )}
        >
          <Grid columns={[1, 1, 2, 2]} spacing={3}>
            {bundle.packages.map(renderPackage)}
          </Grid>
        </Section>
      </Show>
      {featureGroups.map((group) => (
        <FeatureGroupSection
          key={group.category}
          {...group}
          wheelSet={wheelSet}
          currency={bundle.currency}
          canSelect={props.canSelect}
          renderFeature={renderGroupedFeature}
        />
      ))}
      <UnavailableSection
        bundle={bundle}
        items={unavailable}
        canSelect={props.canSelect}
        factoryFreezeReason={props.factoryFreezeReason}
        selectedFeatureIds={selectedFeatureIds}
        selectedPackageIds={selectedPackageIds}
        isPackageBlocked={(item) =>
          !isPackageInTrim(item) ||
          isBlockedByWheelLock(item) ||
          getConflict(item.memberFeatureIds).isBlocked
        }
        toggle={toggle}
      />
    </VStack>
  );
}
